import ApiClient from '@/api/ApiClient';

import type {
  CategoryStatistics,
  CreateManualRequest,
  DepartmentStatistics,
  EmployeeStatistics,
  FilterCriteria,
  GetManualReadResult,
  GetManualResult,
  Manual,
  ManualGroup,
  PagedResultManualGroup,
  PagedResultManualReadWithName,
  PagedResultManualWithName,
  SendManualRequest,
  SortCriteria,
  StatisticsResult,
  SubCategoryStatistics,
} from '../models';

export type SearchManualsRequest = {
  employee?: string;
  employeeDepartmentIds?: string[];
  filterCriteria?: FilterCriteria[];
  includeLimit?: boolean;
  limit?: number;
  skip?: number;
  sortCriteria?: SortCriteria[];
};

export type SearchManualGroupsRequest = {
  employee?: string;
  employeeDepartmentIds?: string[];
  filterCriteria?: FilterCriteria[];
  includeLimit?: boolean;
  limit?: number;
  skip?: number;
  sortCriteria?: SortCriteria[];
};

export type SearchManualReadsRequest = {
  employee?: string;
  employeeDepartmentIds?: string[];
  filterCriteria?: FilterCriteria[];
  includeLimit?: boolean;
  limit?: number;
  skip?: number;
  sortCriteria?: SortCriteria[];
};

export type GetCategoryStatisticsRequest = {
  employee?: string;
  employeeDepartmentIds?: string[];
  filterCriteria?: FilterCriteria[];
  includeLimit?: boolean;
  limit?: number;
  skip?: number;
  sortCriteria?: SortCriteria[];
};

export type GetDepartmentStatisticsRequest = {
  employee?: string;
  employeeDepartmentIds?: string[];
  filterCriteria?: FilterCriteria[];
  includeLimit?: boolean;
  limit?: number;
  skip?: number;
  sortCriteria?: SortCriteria[];
};

export type GetEmployeeStatisticsRequest = {
  employee?: string;
  employeeDepartmentIds?: string[];
  filterCriteria?: FilterCriteria[];
  includeLimit?: boolean;
  limit?: number;
  skip?: number;
  sortCriteria?: SortCriteria[];
};

export type GetManualReadsStatisticsRequest = {
  employee?: string;
  employeeDepartmentIds?: string[];
  filterCriteria?: FilterCriteria[];
  groupBy?: string;
  includeLimit?: boolean;
  limit?: number;
  skip?: number;
  sortCriteria?: SortCriteria[];
};

export type GetSubCategoryStatisticsRequest = {
  employee?: string;
  employeeDepartmentIds?: string[];
  filterCriteria?: FilterCriteria[];
  includeLimit?: boolean;
  limit?: number;
  skip?: number;
  sortCriteria?: SortCriteria[];
};

export class ManualsClient {
  apiClient: ApiClient;

  constructor(apiClient?: ApiClient) {
    this.apiClient = apiClient || ApiClient.instance;
  }

  searchManuals(request: SearchManualsRequest = {}): Promise<PagedResultManualWithName> {
    const { ...params } = request;
    return this.apiClient.get({
      path: '/api/manuals',
      params,
    });
  }

  addManual(request: { body?: CreateManualRequest; } = {}): Promise<Manual> {
    const { body } = request;
    return this.apiClient.put({
      path: '/api/manuals',
      body,
    });
  }

  createManual(request: { body?: CreateManualRequest; } = {}): Promise<Manual> {
    const { body } = request;
    return this.apiClient.post({
      path: '/api/manuals',
      body,
    });
  }

  getManualsByKey(request: { key?: string; } = {}): Promise<Manual> {
    const { ...params } = request;
    return this.apiClient.get({
      path: '/api/manuals/by-key',
      params,
    });
  }

  searchManualGroups(request: SearchManualGroupsRequest = {}): Promise<PagedResultManualGroup> {
    const { ...params } = request;
    return this.apiClient.get({
      path: '/api/manuals/groups',
      params,
    });
  }

  createManualGroup(request: { body?: ManualGroup; } = {}): Promise<ManualGroup> {
    const { body } = request;
    return this.apiClient.put({
      path: '/api/manuals/groups',
      body,
    });
  }

  updateManualGroup(request: { body?: ManualGroup; } = {}): Promise<ManualGroup> {
    const { body } = request;
    return this.apiClient.post({
      path: '/api/manuals/groups',
      body,
    });
  }

  getManualGroup(request: { id: number; }): Promise<ManualGroup> {
    const { id } = request;
    return this.apiClient.get({
      path: `/api/manuals/groups/${id}`,
    });
  }

  searchManualReads(request: SearchManualReadsRequest = {}): Promise<PagedResultManualReadWithName> {
    const { ...params } = request;
    return this.apiClient.get({
      path: '/api/manuals/reads',
      params,
    });
  }

  getCategoryStatistics(request: GetCategoryStatisticsRequest = {}): Promise<CategoryStatistics[]> {
    const { ...params } = request;
    return this.apiClient.get({
      path: '/api/manuals/reads/category-statistics',
      params,
    });
  }

  getDepartmentStatistics(request: GetDepartmentStatisticsRequest = {}): Promise<DepartmentStatistics[]> {
    const { ...params } = request;
    return this.apiClient.get({
      path: '/api/manuals/reads/departments-statistics',
      params,
    });
  }

  getEmployeeStatistics(request: GetEmployeeStatisticsRequest = {}): Promise<EmployeeStatistics[]> {
    const { ...params } = request;
    return this.apiClient.get({
      path: '/api/manuals/reads/employee-statistics',
      params,
    });
  }

  getManualReadsStatistics(request: GetManualReadsStatisticsRequest = {}): Promise<StatisticsResult[]> {
    const { ...params } = request;
    return this.apiClient.get({
      path: '/api/manuals/reads/statistics',
      params,
    });
  }

  getSubCategoryStatistics(request: GetSubCategoryStatisticsRequest = {}): Promise<SubCategoryStatistics[]> {
    const { ...params } = request;
    return this.apiClient.get({
      path: '/api/manuals/reads/subcategory-statistics',
      params,
    });
  }

  getManulReadResult(request: { id: number; }): Promise<GetManualReadResult> {
    const { id } = request;
    return this.apiClient.get({
      path: `/api/manuals/reads/${id}`,
    });
  }

  sendManual(request: { body?: SendManualRequest; } = {}): Promise<void> {
    const { body } = request;
    return this.apiClient.post({
      path: '/api/manuals/send',
      body,
    });
  }

  getManuals(request: { id: number; }): Promise<GetManualResult> {
    const { id } = request;
    return this.apiClient.get({
      path: `/api/manuals/${id}`,
    });
  }

  exportManuals(filter: string, request: SearchManualsRequest = {}): Promise<any> {
    return this.apiClient.getBlob({
      path: `/api/manuals/export?${filter ?? ''}`,
      params: request,
    });
  }

  exportManualGroup(filter: string, request: SearchManualGroupsRequest = {}): Promise<any> {
    return this.apiClient.getBlob({
      path: `/api/manuals/groups/export?${filter ?? ''}`,
      params: request,
    });
  }

  exportManualReads(filter: string, request: SearchManualReadsRequest = {}): Promise<any> {
    return this.apiClient.getBlob({
      path: `/api/manuals/reads/export?${filter ?? ''}`,
      params: request,
    });
  }
}

export default ManualsClient;
